import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../../lib/utils';
import { DATA_TYPE_TINT, FLOW_TINTS, type FlowDataType } from './flows-styles';

export type DataTypeChipProps = Omit<ComponentProps<'span'>, 'children'> & {
  /** Figma "Type": sets the colour. `variant` is a Variant set. */
  type: FlowDataType;
  /** Figma "Label": the type's name, or a count such as "15 scripts". */
  children: ReactNode;
};

/**
 * Typed data on an edge or a port (Figma ↳ Flows ↳ Data type chip, 153:26):
 * radius/full, 3px × 6/8px padding, gap 6, 6px dot in the type's chart colour
 * on a 14% fill, Caption text/primary. Colour = data type; the label carries
 * the meaning, so the dot is decorative.
 */
export function DataTypeChip({ type, className, children, ...props }: DataTypeChipProps) {
  const tint = FLOW_TINTS[DATA_TYPE_TINT[type]];
  return (
    <span
      data-slot="data-type-chip"
      data-type={type}
      className={cn(
        'text-caption text-text-primary inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full py-[3px] pr-2 pl-1.5 whitespace-nowrap',
        tint.soft,
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        data-slot="data-type-chip-dot"
        className={cn('size-1.5 shrink-0 rounded-full', tint.solid)}
      />
      {children}
    </span>
  );
}
